import React, { useMemo } from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVGrid,
  EVTypography,
  EVLinearProgress,
} from 'lib/components';
import clsx from 'lib/clsx';
import { useSelector } from 'lib/redux';
import {
  getProductIdsBySubscriptionSkuName,
  PRIMARY_SUBSCRIPTION_PRODUCTS,
  normalizeSubscriptionProductLabelForMatch,
} from 'features/order/Order.constants';
import { isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import SubscriptionConsumptionBarStyles from './SubscriptionConsumptionBarStyles';

/**
 * Shows consumption bar for the product that was ordered.
 * V2: uses order.primarySubscriptionProductsWithUsage (usedQty + unitsOrdered, includedQty).
 * Legacy: subscription.usedQty + unitsOrdered, subscription.includedQty from userData.subscriptionDashboard.
 */
function SubscriptionConsumptionBar({ productName, unitsOrdered = 1 }) {
  const classes = SubscriptionConsumptionBarStyles();
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const primarySubscriptionProductsWithUsage = useSelector(({ order }) => order.primarySubscriptionProductsWithUsage ?? []);
  const subscriptions = useSelector(({ userData }) => userData?.subscriptionDashboard?.subscriptions ?? []);

  const plan = useMemo(() => {
    if (!productName) return null;
    const units = Number(unitsOrdered) || 1;
    const normalizedProduct = normalizeSubscriptionProductLabelForMatch(productName);
    const matchesProduct = (rawName) => {
      const pName = normalizeSubscriptionProductLabelForMatch(rawName ?? '');
      return normalizedProduct === pName || normalizedProduct.startsWith(`${pName} and `);
    };
    if (isSubscriptionDashboardV2Enabled && primarySubscriptionProductsWithUsage?.length > 0) {
      const product = primarySubscriptionProductsWithUsage.find(
        (p) => matchesProduct(p.name ?? ''),
      );
      // #genai Array.find returns an object or undefined, making this optional-chain guard equivalent.
      if (!product?.isSubscription) return null;
      const totalUnits = Number(product.includedQty) || 0;
      if (totalUnits <= 0) return null;
      const usedBeforeOrder = Number(product.usedQty) || 0;
      const usedAfterOrder = usedBeforeOrder + units;
      return {
        planName: product.name || productName,
        usedUnits: usedAfterOrder,
        totalUnits,
        unitsOrdered: units,
      };
    }
    if (!subscriptions?.length) return null;
    const productFromConstants = PRIMARY_SUBSCRIPTION_PRODUCTS.find(
      (p) => matchesProduct(p.name ?? ''),
    );
    const productId = productFromConstants?.productId;
    if (!productId) return null;

    // #genai Map subscriptionSkuName -> productId(s) so bundle subscription names work too.
    const usageByProductId = {};
    subscriptions.forEach((s) => {
      const name = (s.subscriptionSkuName || '').trim();
      const ids = getProductIdsBySubscriptionSkuName(name);
      if (!ids.length) return;
      ids.forEach((id) => {
        usageByProductId[id] = {
          usedQty: s.usedQty ?? 0,
          includedQty: s.includedQty ?? 0,
        };
      });
    });

    const usage = usageByProductId[productId];
    if (!usage) return null;
    const totalUnits = Number(usage.includedQty) || 0;
    const usedBeforeOrder = Number(usage.usedQty) || 0;
    const usedAfterOrder = usedBeforeOrder + units;
    return {
      planName: productFromConstants?.name || productName,
      usedUnits: usedAfterOrder,
      totalUnits,
      unitsOrdered: units,
    };
  }, [productName, unitsOrdered, isSubscriptionDashboardV2Enabled, primarySubscriptionProductsWithUsage, subscriptions]);

  if (!plan || plan.totalUnits <= 0) return null;

  const usagePercent = Math.round((plan.usedUnits / plan.totalUnits) * 100);

  return (
    <EVGrid className={classes.root}>
      <EVGrid className={classes.planRow}>
        <EVTypography className={classes.planName}>
          {plan.planName}
        </EVTypography>
        <EVTypography className={classes.planUsage}>
          {plan.usedUnits}
          {' / '}
          {plan.totalUnits}
          {' units'}
        </EVTypography>
      </EVGrid>
      <EVLinearProgress
        variant="determinate"
        value={Math.min(usagePercent, 100)}
        className={clsx(classes.progressBar, usagePercent >= 100 && classes.progressBarFull)}
      />
      <EVTypography className={classes.usageCaption}>
        {usagePercent}
        % Usage
      </EVTypography>
    </EVGrid>
  );
}

SubscriptionConsumptionBar.propTypes = {
  productName: PropTypes.string,
  unitsOrdered: PropTypes.number,
};

SubscriptionConsumptionBar.defaultProps = {
  productName: '',
  unitsOrdered: 1,
};

export default SubscriptionConsumptionBar;
